import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { generateBackupApiKey } from '../ApiClient';
import { useFeedbackContext } from '../context/FeedbackContext';
import { useUserContext } from '../context/UserContext';

interface BackupApiKeyDialogProps {
  open: boolean;
  close: () => void;
}

const EXPIRY_OPTIONS = [
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '180 days', value: 180 },
  { label: '365 days', value: 365 },
];

export const BackupApiKeyDialog: React.FC<BackupApiKeyDialogProps> = (
  { open, close }
) => {

  const { user } = useUserContext();
  const { showMessage, showErrorMessage } = useFeedbackContext();

  const [expiresInDays, setExpiresInDays] = React.useState(30);
  const [generating, setGenerating] = React.useState(false);
  const [backupUrl, setBackupUrl] = React.useState('');
  const [apiKey, setApiKey] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setBackupUrl('');
      setApiKey('');
      setExpiresAt('');
      setExpiresInDays(30);
    }
  }, [open]);

  const copyToClipboard = async (value: string) => {
    await navigator.clipboard.writeText(value);
    showMessage('Copied to clipboard');
  };

  const handleGenerate = async () => {
    if (!user) {
      return;
    }
    setGenerating(true);
    const result = await generateBackupApiKey(user, expiresInDays);
    setGenerating(false);
    if ('error' in result) {
      showErrorMessage(result.error);
      return;
    }
    setBackupUrl(result.url);
    setApiKey(result.apiKey);
    setExpiresAt(result.expiresAt);
  };

  return (
    <Dialog open={open} onClose={close} fullWidth>
      <Stack spacing={2}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyIcon color="secondary" /> Backup API key
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Stack spacing={2}>
            <Typography variant="body2">
              Generate a URL and API key that external backup tools can use to download
              a JSON export of all your bookmarks, without logging in.
            </Typography>
            {!apiKey && (
              <TextField
                select
                label="Key expires in"
                size="small"
                fullWidth
                value={expiresInDays}
                onChange={(event) => setExpiresInDays(Number(event.target.value))}
              >
                {EXPIRY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            )}
            {apiKey && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Copy these now - the key will not be shown again. It expires on{' '}
                  {new Date(expiresAt).toLocaleString()}.
                </Typography>
                <TextField
                  label="Backup URL"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={backupUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={() => copyToClipboard(backupUrl)} aria-label="copy backup url">
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  label="API key"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={apiKey}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={() => copyToClipboard(apiKey)} aria-label="copy api key">
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Send the key in an <code>x-api-key</code> header when requesting the backup URL.
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} variant="outlined">Close</Button>
          <Button
            variant="contained" color="secondary"
            disabled={generating}
            onClick={handleGenerate}
          >
            {apiKey ? 'Generate new key' : 'Generate'}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
